import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { getListing, createReaction, sendMessage, swipeListing, getMessages } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import ListingMap from '../components/ListingMap';
import { 
  MapPin, Calendar, Heart, Share2, ChevronLeft, ChevronRight, 
  Check, User, MessageSquare, Home, Loader2, Send, X,
  Euro, ArrowLeft, Sparkles, Eye, Clock, Shield, Users,
  Maximize2, Grid3X3, Lock, Crown, Edit2, Building2
} from 'lucide-react';

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReactModal, setShowReactModal] = useState(false);
  const [reactionMessage, setReactionMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [showMatch, setShowMatch] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  
  // Chat state
  const [existingMessages, setExistingMessages] = useState([]);
  const [hasExistingChat, setHasExistingChat] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [landlordId, setLandlordId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => { 
    const loadData = async () => {
      try {
        const data = await getListing(id);
        setListing(data);
        setLandlordId(data.ownerId);
        
        if (isAuthenticated) {
          // Check if there are existing messages with this landlord for this listing
          if (data.ownerId && user?.id && data.ownerId !== user.id) {
            try {
              const messages = await getMessages(data.id, data.ownerId);
              if (messages && messages.length > 0) {
                setExistingMessages(messages);
                setHasExistingChat(true);
                setHasLiked(true); // If there are messages, user must have liked
              }
            } catch (err) {
              // No existing chat, that's fine
              console.log('No existing chat found');
            }
          }
        }
      } catch (err) {
        setError(language === 'nl' ? 'Niet gevonden' : 'Not found');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isAuthenticated, user?.id, language]);

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [existingMessages]);

  const handleSwipeRight = async () => {
    if (!isAuthenticated) {
      toast({ title: language === 'nl' ? 'Login vereist' : 'Login required', description: language === 'nl' ? 'Log in om te matchen' : 'Log in to match', variant: 'destructive' });
      navigate('/login');
      return;
    }
    
    try {
      const result = await swipeListing(listing.id, 'right');
      setHasLiked(true); // Mark as liked immediately
      
      if (result.matched) {
        setShowMatch(true);
        // Reload messages after matching
        if (landlordId) {
          const messages = await getMessages(listing.id, landlordId);
          if (messages && messages.length > 0) {
            setExistingMessages(messages);
            setHasExistingChat(true);
          }
        }
      } else {
        toast({ title: language === 'nl' ? 'Geliked!' : 'Liked!', description: language === 'nl' ? 'Je bericht is verstuurd naar de verhuurder' : 'Your message has been sent to the landlord' });
        setHasExistingChat(true); // Open chat now available
        // Reload messages after liking
        if (landlordId) {
          const messages = await getMessages(listing.id, landlordId);
          if (messages && messages.length > 0) {
            setExistingMessages(messages);
          }
        }
      }
    } catch (error) {
      if (error.response?.status === 402 || error.response?.status === 403) {
        navigate(`/upgrade-match/${id}`);
      } else {
        toast({ title: language === 'nl' ? 'Fout' : 'Error', variant: 'destructive' });
      }
    }
  };

  const handleOpenChat = () => {
    if (!isAuthenticated) {
      toast({ title: language === 'nl' ? 'Login vereist' : 'Login required', variant: 'destructive' });
      navigate('/login');
      return;
    }
    // Navigate to full-screen chat page
    const params = new URLSearchParams({
      landlordId: landlordId || listing.ownerId,
      title: listing.title,
      landlord: listing.landlord?.name || 'Verhuurder'
    });
    navigate(`/chat/${listing.id}?${params.toString()}`);
  };

  const handleSendChatMessage = async () => {
    if (!newMessage.trim() || !landlordId) return;
    
    setSendingMessage(true);
    try {
      await sendMessage({
        listingId: listing.id,
        receiverId: landlordId,
        content: newMessage.trim()
      });
      
      // Add message to local state
      const newMsg = {
        id: Date.now().toString(),
        senderId: user.id,
        senderName: user.name,
        content: newMessage.trim(),
        createdAt: new Date().toISOString()
      };
      setExistingMessages(prev => [...prev, newMsg]);
      setNewMessage('');
      
      toast({ title: language === 'nl' ? 'Bericht verstuurd!' : 'Message sent!' });
    } catch (error) {
      toast({ title: language === 'nl' ? 'Fout bij versturen' : 'Error sending', variant: 'destructive' });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleReact = () => {
    if (!isAuthenticated) {
      toast({ title: language === 'nl' ? 'Login vereist' : 'Login required', variant: 'destructive' });
      navigate('/login');
      return;
    }
    // FREE FOR ALL - No subscription check needed
    
    // If there's already a chat, navigate to chat page instead
    if (hasExistingChat) {
      const params = new URLSearchParams({
        landlordId: landlordId || listing.ownerId,
        title: listing.title,
        landlord: listing.landlord?.name || 'Verhuurder'
      });
      navigate(`/chat/${listing.id}?${params.toString()}`);
      return;
    }
    
    setReactionMessage(language === 'nl' 
      ? `Hoi ${listing.landlord?.name || ''},\n\nIk heb interesse in je woning "${listing.title}". Kunnen we een bezichtiging plannen?\n\nGroet,\n${user?.name}`
      : `Hi ${listing.landlord?.name || ''},\n\nI'm interested in your property "${listing.title}". Can we schedule a viewing?\n\nBest regards,\n${user?.name}`
    );
    setShowReactModal(true);
  };

  const submitReaction = async () => {
    if (!reactionMessage.trim()) return;
    setSubmitting(true);
    try {
      await createReaction({ listingId: listing.id, message: reactionMessage });
      if (listing.ownerId && listing.ownerId !== user?.id) {
        await sendMessage({ listingId: listing.id, receiverId: listing.ownerId, content: reactionMessage }).catch(() => {});
      }
      toast({ title: language === 'nl' ? 'Verstuurd!' : 'Sent!', description: language === 'nl' ? 'Bekijk je berichten in het dashboard' : 'Check your messages in the dashboard' });
      setShowReactModal(false);
      setReactionMessage('');
      
      // Set hasExistingChat to true after sending first message
      setHasExistingChat(true);
      setExistingMessages([{
        id: Date.now().toString(),
        senderId: user.id,
        senderName: user.name,
        content: reactionMessage,
        createdAt: new Date().toISOString()
      }]);
    } catch (error) {
      // FREE FOR ALL - No subscription_required check needed
      toast({ title: language === 'nl' ? 'Fout' : 'Error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const nextImage = () => listing.images?.length > 1 && setCurrentImageIndex((p) => (p + 1) % listing.images.length);
  const prevImage = () => listing.images?.length > 1 && setCurrentImageIndex((p) => (p - 1 + listing.images.length) % listing.images.length);

  // Loading
  if (loading) return (
    <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center">
      <div className="text-center">
        <div className="w-20 h-20 bg-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Home className="w-10 h-10 text-white" />
        </div>
        <p className="text-[#9E9E9E]">Laden...</p>
      </div>
    </div>
  );

  // Error
  if (error || !listing) return (
    <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center p-4">
      <div className="text-center">
        <div className="w-20 h-20 bg-[#E5E5E5] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Home className="w-10 h-10 text-[#9E9E9E]" />
        </div>
        <h1 className="text-2xl font-bold text-[#2A2A2A] mb-4">Niet gevonden</h1>
        <Button onClick={() => navigate('/swipematch/swipe')} className="bg-[#2E7D32] text-white rounded-full">
          Terug naar swipen
        </Button>
      </div>
    </div>
  );

  const typeLabels = { kamer: 'Kamer', studio: 'Studio', appartement: 'Appartement' };
  const images = listing.images?.length > 0 ? listing.images : ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'];
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Helper to get full image URL
  const getImageUrl = (img) => {
    if (!img) return 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  return (
    <div className="fixed inset-0 bg-[#F7F7F7] overflow-hidden" data-testid="listing-detail-page">
      <Toaster />

      {/* Full Screen Gallery */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black" data-testid="photo-gallery">
          <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
            <button 
              onClick={() => setShowGallery(false)}
              className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <span className="text-white font-medium">{currentImageIndex + 1} / {images.length}</span>
            <div className="w-10" />
          </div>

          <div className="h-full flex items-center justify-center p-4">
            <img 
              src={getImageUrl(images[currentImageIndex])}
              alt={`Foto ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex gap-2 justify-center overflow-x-auto py-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      i === currentImageIndex ? 'border-white scale-110' : 'border-transparent opacity-60'
                    }`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          {images.length > 1 && (
            <>
              <button 
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button 
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}
        </div>
      )}

      {/* Match Animation */}
      {showMatch && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#F7F7F7]/95 backdrop-blur-sm p-4">
          <div className="text-center max-w-sm">
            <div className="relative mb-8">
              <div className="w-32 h-32 bg-[#2E7D32] rounded-3xl flex items-center justify-center mx-auto shadow-xl">
                <Heart className="w-16 h-16 text-white fill-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-14 h-14 bg-[#1F3A5F] rounded-xl flex items-center justify-center animate-bounce">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#2A2A2A] mb-4">Match!</h2>
            <p className="text-[#9E9E9E] mb-8">Je bericht is verstuurd naar {listing.landlord?.name || 'de verhuurder'}</p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/dashboard')} className="bg-[#2E7D32] text-white h-14 rounded-full font-bold">
                Bekijk matches
              </Button>
              <Button onClick={() => setShowMatch(false)} variant="outline" className="border-[#E5E5E5] h-12 rounded-full">
                Verder kijken
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="h-full overflow-y-auto pb-24">
        {/* Sticky Header with Price */}
        <div className="sticky top-0 z-40 bg-white border-b border-[#E5E5E5]">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)} 
              className="w-10 h-10 bg-[#F7F7F7] rounded-full flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-[#2A2A2A]" />
            </button>
            
            <div className="text-center">
              <div className="text-2xl font-black text-[#2E7D32]">€{listing.price}<span className="text-sm font-normal text-[#9E9E9E]">/mnd {listing.utilitiesIncluded === true ? 'incl.' : listing.utilitiesIncluded === false ? 'excl.' : ''}</span></div>
            </div>
          </div>
        </div>

        {/* Photo Gallery Grid */}
        <div className="bg-white">
          <div className="max-w-4xl mx-auto">
            {/* Main Image + Grid */}
            <div className="grid grid-cols-4 gap-1 sm:gap-2 p-1 sm:p-2">
              {/* Main large image */}
              <div 
                className="col-span-4 sm:col-span-2 sm:row-span-2 relative aspect-[4/3] sm:aspect-square cursor-pointer group"
                onClick={() => { setCurrentImageIndex(0); setShowGallery(true); }}
              >
                <img 
                  src={getImageUrl(images[0])}
                  alt={listing.title}
                  className="w-full h-full object-cover rounded-lg sm:rounded-xl"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg sm:rounded-xl" />
                
                {/* Mobile: Show count on main image */}
                {images.length > 1 && (
                  <button 
                    className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2"
                    onClick={(e) => { e.stopPropagation(); setShowGallery(true); }}
                  >
                    <Grid3X3 className="w-4 h-4" />
                    {images.length} foto's
                  </button>
                )}
              </div>

              {/* Smaller images - desktop only */}
              {images.slice(1, 5).map((img, i) => (
                <div 
                  key={i}
                  className="hidden sm:block relative aspect-square cursor-pointer group"
                  onClick={() => { setCurrentImageIndex(i + 1); setShowGallery(true); }}
                >
                  <img 
                    src={getImageUrl(img)}
                    alt={`Foto ${i + 2}`}
                    className="w-full h-full object-cover rounded-xl"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl" />
                  
                  {/* Show "more" overlay on last visible image */}
                  {i === 3 && images.length > 5 && (
                    <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">+{images.length - 5}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Title & Location */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge className="bg-[#1F3A5F] text-white border-0 text-xs px-3 py-1">
                {typeLabels[listing.type] || 'Woning'}
              </Badge>
              {listing.furnished && (
                <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-0 text-xs px-3 py-1">Gemeubileerd</Badge>
              )}
              <Badge className="bg-[#E5E5E5] text-[#2A2A2A] border-0 text-xs px-3 py-1">
                {listing.size}m²
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">{listing.title}</h1>
            
            <div className="flex items-center gap-2 text-[#9E9E9E]">
              <MapPin className="w-4 h-4 text-[#2E7D32]" />
              <span className="text-base">{listing.neighborhood}, {listing.city}</span>
            </div>
          </div>

          {/* Quick Stats - Horizontal Scroll on Mobile */}
          <div className="flex gap-3 overflow-x-auto pb-2 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-4">
            {[
              { value: `${listing.size}m²`, label: 'Oppervlakte', icon: '📐' },
              { value: `€${listing.deposit || listing.price}`, label: 'Borg', icon: '🔐' },
              { value: listing.roommates || '0', label: 'Huisgenoten', icon: '👥' },
              { value: new Date(listing.available).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }), label: 'Beschikbaar', icon: '📅' }
            ].map((stat, i) => (
              <div key={i} className="flex-shrink-0 w-[120px] sm:w-auto bg-white rounded-2xl p-4 border border-[#E5E5E5] text-center">
                <div className="text-2xl mb-1">{stat.icon}</div>
                <div className="text-lg font-black text-[#2A2A2A]">{stat.value}</div>
                <div className="text-xs text-[#9E9E9E]">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {[
              { id: 'info', label: 'Info' },
              { id: 'kosten', label: 'Kosten' },
              { id: 'verhuurder', label: 'Verhuurder' },
              { id: 'locatie', label: 'Locatie' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#2E7D32] text-white shadow-sm'
                    : 'bg-white text-[#2A2A2A] border border-[#E5E5E5] hover:border-[#2E7D32]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-4">
            {activeTab === 'info' && (
              <>
                {/* Description */}
                <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                  <h3 className="text-lg font-bold text-[#2A2A2A] mb-3">Over deze woning</h3>
                  <p className="text-[#666] leading-relaxed whitespace-pre-line">
                    {listing.description || 'Geen beschrijving beschikbaar.'}
                  </p>
                </div>

                {/* Features */}
                <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                  <h3 className="text-lg font-bold text-[#2A2A2A] mb-4">Kenmerken</h3>
                  
                  {/* Property Features from landlord */}
                  {listing.propertyFeatures && Object.keys(listing.propertyFeatures).length > 0 ? (
                    <div className="space-y-4">
                      {/* Outdoor */}
                      {(listing.propertyFeatures.balcony || listing.propertyFeatures.garden || listing.propertyFeatures.storage) && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#9E9E9E] mb-2">🏡 Buiten</h4>
                          <div className="flex flex-wrap gap-2">
                            {listing.propertyFeatures.balcony && (
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                                Balkon {listing.propertyFeatures.balconySize ? `(${listing.propertyFeatures.balconySize}m²)` : ''}
                              </span>
                            )}
                            {listing.propertyFeatures.garden && (
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                                Tuin {listing.propertyFeatures.gardenSize ? `(${listing.propertyFeatures.gardenSize}m²)` : ''}
                              </span>
                            )}
                            {listing.propertyFeatures.storage && (
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">Berging</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Shared/Private */}
                      {(listing.propertyFeatures.kitchen || listing.propertyFeatures.shower || listing.propertyFeatures.toilet || listing.propertyFeatures.livingroom) && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#9E9E9E] mb-2">🚿 Gedeeld/Eigen</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {listing.propertyFeatures.kitchen && (
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${listing.propertyFeatures.kitchen === 'eigen' ? 'bg-green-500' : 'bg-orange-500'}`} />
                                <span className="text-sm">Keuken: {listing.propertyFeatures.kitchen === 'eigen' ? 'Eigen' : 'Gedeeld'}</span>
                              </div>
                            )}
                            {listing.propertyFeatures.shower && (
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${listing.propertyFeatures.shower === 'eigen' ? 'bg-green-500' : 'bg-orange-500'}`} />
                                <span className="text-sm">Douche: {listing.propertyFeatures.shower === 'eigen' ? 'Eigen' : 'Gedeeld'}</span>
                              </div>
                            )}
                            {listing.propertyFeatures.toilet && (
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${listing.propertyFeatures.toilet === 'eigen' ? 'bg-green-500' : 'bg-orange-500'}`} />
                                <span className="text-sm">Toilet: {listing.propertyFeatures.toilet === 'eigen' ? 'Eigen' : 'Gedeeld'}</span>
                              </div>
                            )}
                            {listing.propertyFeatures.livingroom && (
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${listing.propertyFeatures.livingroom === 'eigen' ? 'bg-green-500' : 'bg-orange-500'}`} />
                                <span className="text-sm">Woonkamer: {listing.propertyFeatures.livingroom === 'eigen' ? 'Eigen' : 'Gedeeld'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Amenities */}
                      <div>
                        <h4 className="text-sm font-semibold text-[#9E9E9E] mb-2">🏠 Voorzieningen</h4>
                        <div className="flex flex-wrap gap-2">
                          {listing.propertyFeatures.elevator && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Lift</span>}
                          {listing.propertyFeatures.washingConnection && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Wasmachine</span>}
                          {listing.propertyFeatures.dryerConnection && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Droger</span>}
                          {listing.propertyFeatures.dishwasher && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Vaatwasser</span>}
                          {listing.propertyFeatures.internetIncluded && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Internet incl.</span>}
                          {listing.propertyFeatures.airco && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Airco</span>}
                          {listing.propertyFeatures.solarPanels && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Zonnepanelen</span>}
                          {listing.propertyFeatures.smartHome && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Smart home</span>}
                          {listing.propertyFeatures.alarmSystem && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Alarm</span>}
                          {listing.propertyFeatures.videoIntercom && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Video intercom</span>}
                          {listing.propertyFeatures.bikeStorage && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Fietsenstalling</span>}
                          {listing.propertyFeatures.kitchenType && <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm capitalize">Keuken: {listing.propertyFeatures.kitchenType}</span>}
                          {listing.propertyFeatures.energyLabel && <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm">Energie: {listing.propertyFeatures.energyLabel}</span>}
                        </div>
                      </div>

                      {/* Bathroom */}
                      {(listing.propertyFeatures.bathtub || listing.propertyFeatures.showerBathroom || listing.propertyFeatures.sink || listing.propertyFeatures.toiletInBathroom) && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#9E9E9E] mb-2">🛁 Badkamer</h4>
                          <div className="flex flex-wrap gap-2">
                            {listing.propertyFeatures.bathtub && <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Bad</span>}
                            {listing.propertyFeatures.showerBathroom && <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Douche</span>}
                            {listing.propertyFeatures.sink && <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Wastafel</span>}
                            {listing.propertyFeatures.toiletInBathroom && <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Toilet</span>}
                          </div>
                        </div>
                      )}

                      {/* Parking */}
                      {(listing.propertyFeatures.parkingIncluded || listing.propertyFeatures.parkingAvailable || listing.propertyFeatures.parkingPermitNeeded || listing.propertyFeatures.noParking) && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#9E9E9E] mb-2">🚗 Parkeren</h4>
                          <div className="flex flex-wrap gap-2">
                            {listing.propertyFeatures.parkingIncluded && <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">Parkeerplaats inclusief</span>}
                            {listing.propertyFeatures.parkingAvailable && <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm">Parkeerplaats beschikbaar</span>}
                            {listing.propertyFeatures.parkingPermitNeeded && <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">Vergunning nodig</span>}
                            {listing.propertyFeatures.noParking && <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">Geen parkeren</span>}
                          </div>
                        </div>
                      )}

                      {/* Location */}
                      {(listing.propertyFeatures.publicTransport || listing.propertyFeatures.supermarketNearby || listing.propertyFeatures.schoolNearby || listing.propertyFeatures.neighborhoodType) && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#9E9E9E] mb-2">📍 Locatie</h4>
                          <div className="flex flex-wrap gap-2">
                            {listing.propertyFeatures.publicTransport && <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">OV dichtbij</span>}
                            {listing.propertyFeatures.supermarketNearby && <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">Supermarkt dichtbij</span>}
                            {listing.propertyFeatures.schoolNearby && <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">School dichtbij</span>}
                            {listing.propertyFeatures.neighborhoodType && <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm capitalize">Buurt: {listing.propertyFeatures.neighborhoodType}</span>}
                          </div>
                        </div>
                      )}

                      {/* Smoking */}
                      {listing.propertyFeatures.smokingAllowed && (
                        <div className="flex items-center gap-2 text-orange-600 text-sm">
                          <span>🚬</span> Roken toegestaan
                        </div>
                      )}
                    </div>
                  ) : (
                    // Fallback to old features display
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Gemeubileerd', available: listing.furnished },
                        { label: 'Internet', available: listing.internet !== false },
                        { label: 'Huisdieren toegestaan', available: listing.petsAllowed },
                        { label: 'Roken toegestaan', available: listing.smokingAllowed }
                      ].map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            feature.available ? 'bg-[#2E7D32] text-white' : 'bg-[#E5E5E5] text-[#9E9E9E]'
                          }`}>
                            {feature.available ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          </div>
                          <span className={`text-sm ${feature.available ? 'text-[#2A2A2A]' : 'text-[#9E9E9E]'}`}>{feature.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'kosten' && (
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <h3 className="text-lg font-bold text-[#2A2A2A] mb-4">Kosten overzicht</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                    <span className="text-[#666]">Huur per maand</span>
                    <span className="text-2xl font-black text-[#2E7D32]">€{listing.price}</span>
                  </div>
                  {listing.deposit && (
                    <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#666]">Borg (eenmalig)</span>
                      <span className="font-bold text-[#2A2A2A]">€{listing.deposit}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                    <span className="text-[#666]">Gas, water, licht (GWL)</span>
                    <span className={`font-bold ${listing.utilitiesIncluded ? 'text-[#2E7D32]' : 'text-orange-600'}`}>
                      {listing.utilitiesIncluded === true 
                        ? 'Inclusief' 
                        : listing.utilitiesIncluded === false
                          ? listing.utilitiesCost 
                            ? `Exclusief (+€${listing.utilitiesCost}/mnd)` 
                            : 'Exclusief (apart)'
                          : 'Onbekend'
                      }
                    </span>
                  </div>
                  {listing.serviceCost > 0 && (
                    <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#666]">Servicekosten</span>
                      <span className="font-bold text-[#2A2A2A]">€{listing.serviceCost}/mnd</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 bg-[#F7F7F7] rounded-xl px-4 mt-4">
                    <span className="font-bold text-[#2A2A2A]">Totaal per maand (excl. borg)</span>
                    <span className="text-xl font-black text-[#2E7D32]">
                      €{(listing.price || 0) + (listing.utilitiesIncluded === false ? (listing.utilitiesCost || 0) : 0) + (listing.serviceCost || 0)}
                    </span>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-[#2E7D32]/10 rounded-xl">
                  <div className="flex items-center gap-2 text-[#2E7D32] font-medium text-sm">
                    <Shield className="w-5 h-5" />
                    Veilig betalen via Swipe je woning
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'verhuurder' && (
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                {/* Profile header with photo */}
                <div className="flex items-center gap-4 mb-4">
                  {listing.landlord?.profileImage ? (
                    <img 
                      src={listing.landlord.profileImage} 
                      alt={listing.landlord?.name} 
                      className="w-20 h-20 rounded-full object-cover border-4 border-[#2E7D32]/20"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center text-white text-3xl font-bold">
                      {(listing.landlord?.name || 'V')[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#2A2A2A]">{listing.landlord?.name || 'Verhuurder'}</h3>
                    {listing.landlord?.businessName && (
                      <p className="text-sm text-[#1F3A5F] font-medium">{listing.landlord.businessName}</p>
                    )}
                    <div className="flex items-center gap-2 text-sm mt-1">
                      {listing.landlord?.verified && (
                        <span className="text-[#2E7D32] flex items-center gap-1">
                          <Check className="w-4 h-4" /> Geverifieerd
                        </span>
                      )}
                      {user?.id === listing.ownerId && (
                        <span className="text-[#1F3A5F] flex items-center gap-1 ml-2">
                          {language === 'nl' ? '(Dit ben jij)' : '(This is you)'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* KVK Number if available */}
                {listing.landlord?.kvkNumber && (
                  <div className="mb-4 p-3 bg-[#F7F7F7] rounded-xl">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#1F3A5F]" />
                      <span className="text-sm text-[#9E9E9E]">KVK:</span>
                      <span className="text-sm font-medium text-[#2A2A2A]">{listing.landlord.kvkNumber}</span>
                    </div>
                  </div>
                )}

                {/* Landlord Bio/Description */}
                {listing.landlord?.bio && (
                  <div className="mb-4 p-4 bg-[#F7F7F7] rounded-xl">
                    <p className="text-sm font-medium text-[#2A2A2A] mb-1">{language === 'nl' ? 'Over de verhuurder' : 'About the landlord'}</p>
                    <p className="text-sm text-[#666] leading-relaxed">{listing.landlord.bio}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="text-center p-4 bg-[#F7F7F7] rounded-xl">
                    <div className="text-2xl font-black text-[#2A2A2A]">{listing.landlord?.timesRented || 0}</div>
                    <div className="text-xs text-[#9E9E9E]">Keer verhuurd</div>
                  </div>
                  <div className="text-center p-4 bg-[#F7F7F7] rounded-xl">
                    <div className="text-2xl font-black text-[#2A2A2A]">&lt;24u</div>
                    <div className="text-xs text-[#9E9E9E]">Reactietijd</div>
                  </div>
                </div>

                {/* Show existing chat indicator if user has contact */}
                {user?.id !== listing.ownerId && hasExistingChat && (
                  <div className="p-3 bg-[#2E7D32]/10 rounded-xl">
                    <div className="flex items-center gap-2 text-[#2E7D32]">
                      <Check className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {language === 'nl' ? 'Je hebt al contact met deze verhuurder' : 'You are already in contact with this landlord'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'locatie' && (
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-6 h-6 text-[#2E7D32]" />
                  <div>
                    <h3 className="font-bold text-[#2A2A2A]">{listing.neighborhood}</h3>
                    <p className="text-[#9E9E9E]">{listing.city}</p>
                  </div>
                </div>
                <ListingMap city={listing.city} neighborhood={listing.neighborhood} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-14 h-14 bg-[#F7F7F7] rounded-full flex items-center justify-center border border-[#E5E5E5] hover:border-[#C04A4A] hover:bg-[#C04A4A]/5 transition-colors"
            >
              <X className="w-6 h-6 text-[#C04A4A]" />
            </button>
            
            {/* Only show Chat button if NOT the owner of the listing */}
            {user?.id !== listing.ownerId ? (
              <>
                <Button 
                  onClick={handleOpenChat}
                  className="flex-1 bg-[#1F3A5F] hover:bg-[#0D2137] text-white h-14 rounded-full font-bold text-lg"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  {hasExistingChat 
                    ? (language === 'nl' ? 'Open chat' : 'Open chat')
                    : 'Chat'
                  }
                  {hasExistingChat && existingMessages.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                      {existingMessages.length}
                    </span>
                  )}
                </Button>
                
                {/* Only show like button if NOT already liked */}
                {!hasLiked && !hasExistingChat && (
                  <button
                    onClick={handleSwipeRight}
                    className="w-14 h-14 bg-[#2E7D32] rounded-full flex items-center justify-center hover:bg-[#256B2B] transition-colors shadow-lg"
                    data-testid="like-btn"
                  >
                    <Heart className="w-6 h-6 text-white" />
                  </button>
                )}
              </>
            ) : (
              /* Owner viewing their own listing - show edit button instead */
              <Button 
                onClick={() => navigate(`/verhuurder/woning/${listing.id}/bewerk`)}
                className="flex-1 bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 rounded-full font-bold text-lg"
              >
                <Edit2 className="w-5 h-5 mr-2" />
                {language === 'nl' ? 'Bewerken' : 'Edit'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* React Modal - for first message */}
      <Dialog open={showReactModal} onOpenChange={setShowReactModal}>
        <DialogContent className="sm:max-w-[450px] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-[#2A2A2A]">
              {language === 'nl' ? 'Bericht aan' : 'Message to'} {listing.landlord?.name || (language === 'nl' ? 'verhuurder' : 'landlord')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              placeholder={language === 'nl' ? 'Schrijf je bericht...' : 'Write your message...'}
              className="min-h-[150px] resize-none border-[#E5E5E5] focus:ring-[#2E7D32] rounded-xl"
              value={reactionMessage}
              onChange={(e) => setReactionMessage(e.target.value)}
            />
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowReactModal(false)}
                className="flex-1 h-12 rounded-full border-[#E5E5E5]"
              >
                {language === 'nl' ? 'Annuleren' : 'Cancel'}
              </Button>
              <Button 
                onClick={submitReaction}
                disabled={submitting || !reactionMessage.trim()}
                className="flex-1 bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 rounded-full font-bold"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" />{language === 'nl' ? 'Versturen' : 'Send'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Modal - for existing conversations */}
      <Dialog open={showChatModal} onOpenChange={setShowChatModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] rounded-3xl p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] p-4 text-white">
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {language === 'nl' ? 'Chat met' : 'Chat with'} {listing.landlord?.name || (language === 'nl' ? 'Verhuurder' : 'Landlord')}
            </DialogTitle>
            <p className="text-white/70 text-sm mt-1">{listing.title}</p>
          </div>
          
          {/* Messages */}
          <div className="h-[300px] overflow-y-auto p-4 space-y-3 bg-[#F7F7F7]">
            {existingMessages.length === 0 ? (
              <div className="text-center text-[#9E9E9E] py-8">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{language === 'nl' ? 'Nog geen berichten' : 'No messages yet'}</p>
              </div>
            ) : (
              existingMessages.map((msg, idx) => (
                <div 
                  key={msg.id || idx}
                  className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-2xl p-3 ${
                    msg.senderId === user?.id 
                      ? 'bg-[#2E7D32] text-white rounded-br-md' 
                      : 'bg-white text-[#2A2A2A] border border-[#E5E5E5] rounded-bl-md'
                  }`}>
                    {msg.senderId !== user?.id && (
                      <p className="text-xs font-bold mb-1 text-[#2E7D32]">{msg.senderName}</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.senderId === user?.id ? 'text-white/60' : 'text-[#9E9E9E]'}`}>
                      {new Date(msg.createdAt).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        day: 'numeric',
                        month: 'short',
                        timeZone: 'Europe/Amsterdam'
                      })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          
          {/* Input or Paywall */}
          <div className="p-4 border-t border-[#E5E5E5] bg-white">
            {user?.subscription === 'swipematch' || user?.trialEndDate ? (
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={language === 'nl' ? 'Typ een bericht...' : 'Type a message...'}
                  className="flex-1 h-12 rounded-full border-[#E5E5E5]"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChatMessage()}
                />
                <Button
                  onClick={handleSendChatMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="w-12 h-12 bg-[#2E7D32] hover:bg-[#256B2B] rounded-full p-0"
                >
                  {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            ) : (
              /* Premium Required Paywall - Clear and conversion-focused */
              <div className="bg-gradient-to-r from-[#2E7D32]/10 to-[#1F3A5F]/10 rounded-2xl p-4 border-2 border-dashed border-[#2E7D32]/40">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-black text-[#2A2A2A] text-base leading-tight">
                      {language === 'nl' ? '🔒 Premium vereist om te chatten' : '🔒 Premium required to chat'}
                    </p>
                    <p className="text-sm text-[#9E9E9E] mt-1">
                      {language === 'nl' 
                        ? 'Neem een abonnement om verder te praten met de verhuurder'
                        : 'Subscribe to continue chatting with the landlord'
                      }
                    </p>
                  </div>
                </div>

                {/* Blocked input preview */}
                <div className="bg-white/70 rounded-xl p-3 mb-4 border border-[#E5E5E5] opacity-60">
                  <div className="flex items-center gap-2 text-sm text-[#9E9E9E]">
                    <MessageSquare className="w-4 h-4" />
                    <span className="blur-sm select-none">
                      {language === 'nl' ? 'Typ hier je bericht...' : 'Type your message here...'}
                    </span>
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-white rounded-xl p-3 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                      <span className="text-[#2E7D32] font-bold">✓</span> 
                      {language === 'nl' ? 'Onbeperkt chatten' : 'Unlimited chat'}
                    </div>
                    <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                      <span className="text-[#2E7D32] font-bold">✓</span> 
                      {language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes'}
                    </div>
                    <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                      <span className="text-[#2E7D32] font-bold">✓</span> 
                      {language === 'nl' ? 'Direct contact' : 'Direct contact'}
                    </div>
                    <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                      <span className="text-[#2E7D32] font-bold">✓</span> 
                      {language === 'nl' ? 'Profiel boost' : 'Profile boost'}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => navigate(`/upgrade/${listing.id}`, { state: { listing } })}
                  className="w-full bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg hover:opacity-90"
                  data-testid="chat-paywall-upgrade-btn"
                >
                  <Crown className="w-5 h-5" />
                  {language === 'nl' ? 'Upgrade naar Premium - €4,99/week' : 'Upgrade to Premium - €4.99/wk'}
                </Button>
                <p className="text-center text-xs text-[#9E9E9E] mt-2">
                  {language === 'nl' ? '✨ Direct opzegbaar • Geen verrassingen' : '✨ Cancel anytime • No surprises'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingDetailPage;
